import { httpClient } from '@/shared/api';
import type {
  GovernorateDto,
  CityDto,
  BudgetSummaryDto,
  BudgetByGovernorateRowDto,
  BudgetByDayRowDto,
  BudgetListParams,
  BudgetOverviewMainDashboardDto,
  BudgetWithFiltersParams,
  BudgetWithFiltersResponseDto,
  TuitionStatisticsDto,
  BudgetOverviewPagedDto,
} from './budgets.dto';

const DASHBOARD_PREFIX = '/dashboard/budgets';
const PREFIX = '/app/budget';
const BUDGET_OVERVIEW_PREFIX = '/app/budget-overview';

function toQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '' && value !== null) {
      search.set(key, String(value));
    }
  }
  const q = search.toString();
  return q ? `?${q}` : '';
}

function toQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') search.set(k, String(v));
  }
  const q = search.toString();
  return q ? `?${q}` : '';
}

export const budgetsApi = {
  getYears: () => httpClient.get<number[]>(`${DASHBOARD_PREFIX}/years`),

  /** Searchable years from ABP budget endpoint */
  getBudgetYears: (params: { year?: string; skipCount?: number; maxResultCount?: number }) =>
    httpClient.get<number[]>(
      `${PREFIX}/budget-years${toQuery({
        skipCount: params.skipCount ?? 0,
        maxResultCount: params.maxResultCount ?? 100,
        year: params.year,
      })}`
    ),

  getGovernorates: () =>
    httpClient.get<GovernorateDto[]>(`${DASHBOARD_PREFIX}/governorates`),

  getCities: (governorateId?: string) => {
    const q = governorateId ? `?governorateId=${encodeURIComponent(governorateId)}` : '';
    return httpClient.get<CityDto[]>(`${DASHBOARD_PREFIX}/cities${q}`);
  },

  getSummary: (params: { year?: string; governorateId?: string; cityId?: string }) =>
    httpClient.get<BudgetSummaryDto>(`${DASHBOARD_PREFIX}/summary${toQueryString(params)}`),

  /** Main dashboard KPIs - Year required, GovernorateId optional */
  getBudgetOverviewMainDashboard: (params: {
    year: number;
    governorateId?: number;
  }) => {
    const q = new URLSearchParams();
    q.set('Year', String(params.year));
    if (params.governorateId != null) q.set('GovernorateId', String(params.governorateId));
    return httpClient.get<BudgetOverviewMainDashboardDto>(
      `${BUDGET_OVERVIEW_PREFIX}/budget-overview-main-dashboard?${q.toString()}`
    );
  },

  /** GET /api/app/budget-overview/tuition-statistics — Year required */
  getTuitionStatistics: (params: {
    year: number;
    governorateId?: number;
    sorting?: string;
    skipCount?: number;
    maxResultCount?: number;
  }) => {
    const q = new URLSearchParams();
    q.set('Year', String(params.year));
    if (params.governorateId != null) q.set('GovernorateId', String(params.governorateId));
    if (params.sorting != null && params.sorting !== '') q.set('Sorting', params.sorting);
    if (params.skipCount != null) q.set('SkipCount', String(params.skipCount));
    if (params.maxResultCount != null) q.set('MaxResultCount', String(params.maxResultCount));
    return httpClient.get<TuitionStatisticsDto>(
      `${BUDGET_OVERVIEW_PREFIX}/tuition-statistics?${q.toString()}`
    );
  },

  /** GET /api/app/budget-overview — Year required */
  getBudgetOverview: (params: {
    year: number;
    governorateId?: number;
    sorting?: string;
    skipCount?: number;
    maxResultCount?: number;
  }) => {
    const q = new URLSearchParams();
    q.set('Year', String(params.year));
    if (params.governorateId != null) q.set('GovernorateId', String(params.governorateId));
    if (params.sorting != null && params.sorting !== '') q.set('Sorting', params.sorting);
    if (params.skipCount != null) q.set('SkipCount', String(params.skipCount));
    if (params.maxResultCount != null) q.set('MaxResultCount', String(params.maxResultCount));
    return httpClient.get<BudgetOverviewPagedDto>(
      `${BUDGET_OVERVIEW_PREFIX}?${q.toString()}`
    );
  },

  getByGovernorate: (params: { year?: string; governorateId?: string; cityId?: string }) =>
    httpClient.get<BudgetByGovernorateRowDto[]>(`${DASHBOARD_PREFIX}/by-governorate${toQueryString(params)}`),

  getByDay: (params: { year?: string; governorateId?: string; cityId?: string }) =>
    httpClient.get<BudgetByDayRowDto[]>(`${DASHBOARD_PREFIX}/by-day${toQueryString(params)}`),

  /** Map BudgetListParams (from dashboard drill-down) to API params */
  listParamsToWithFilters: (params: BudgetListParams): BudgetWithFiltersParams => {
    const api: BudgetWithFiltersParams = {
      SkipCount: 0,
      MaxResultCount: 500,
    };
    if (params.year) api.Year = parseInt(params.year, 10);
    if (params.status !== undefined) api.BudgetStatus = params.status;
    if (params.governorateId) api.GovernorateId = parseInt(params.governorateId, 10);
    if (params.date) api.SubmittedDate = params.date;
    return api;
  },

  /** GET /api/app/budget/with-filters - paginated budget list with filters */
  getWithFilters: (params: BudgetWithFiltersParams) => {
    const q = new URLSearchParams();
    if (params.SchoolId != null) q.set('SchoolId', params.SchoolId);
    if (params.Year != null) q.set('Year', String(params.Year));
    if (params.BudgetStatus != null) q.set('BudgetStatus', String(params.BudgetStatus));
    if (params.UnderPreparation != null) q.set('UnderPreparation', String(params.UnderPreparation));
    if (params.Sorting != null) q.set('Sorting', params.Sorting);
    if (params.SkipCount != null) q.set('SkipCount', String(params.SkipCount));
    if (params.MaxResultCount != null) q.set('MaxResultCount', String(params.MaxResultCount));
    if (params.GovernorateId != null) q.set('GovernorateId', String(params.GovernorateId));
    if (params.SubmittedDate != null) q.set('SubmittedDate', params.SubmittedDate);
    const query = q.toString();
    return httpClient.get<BudgetWithFiltersResponseDto>(
      `${PREFIX}/with-filters${query ? `?${query}` : ''}`
    );
  },
};
